import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import './Sidebar.css';
import { useTranslation } from 'react-i18next';
import { FaHome, FaSms, FaUsers, FaShippingFast, FaRobot, FaStore, FaChartBar, FaArchive, FaUserTie, FaCog, FaPowerOff, FaExchangeAlt, FaShoppingCart, FaTag, FaComments, FaChevronDown, FaBell, FaShopify, FaTruck, FaTicketAlt, FaBook, FaWarehouse, FaCode, FaBolt, FaProjectDiagram, FaEye, FaMoneyBillWave, FaPhoneAlt, FaClock, FaTasks, FaBoxOpen, FaPlug, FaDatabase, FaEllipsisV, FaChartLine, FaHeadset, FaListUl, FaBuilding, FaUser, FaMapMarkedAlt, FaFlagCheckered } from 'react-icons/fa';
import NotificationBell from './NotificationBell';
import BrandMark from './BrandMark';
import axios from 'axios';
import { ONBOARDING_EVENT } from './onboarding/events';

const getStoredUserData = () => {
  const storedUserData = localStorage.getItem('userData');

  if (!storedUserData) {
    return null;
  }

  try {
    return JSON.parse(storedUserData);
  } catch (error) {
    console.error('Failed to parse stored user data:', error);
    return null;
  }
};

// Collapsed groups by default except the daily-work ones; the user's own
// open/closed choices persist across sessions.
const SECTIONS_STORAGE_KEY = 'una_sidebar_sections';
const DEFAULT_SECTIONS = {
  tickets: true,
  orders: true,
  pilotage: true,
  clients: false,
  carriers: false,
  agents: false,
  voice: false,
  channels: false,
  data: false,
  catalogue: false,
  settings: false,
};

const getStoredSections = () => {
  try {
    const stored = JSON.parse(localStorage.getItem(SECTIONS_STORAGE_KEY));
    return stored && typeof stored === 'object' ? { ...DEFAULT_SECTIONS, ...stored } : DEFAULT_SECTIONS;
  } catch (error) {
    return DEFAULT_SECTIONS;
  }
};

const Sidebar = ({ userRole }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [userData, setUserData] = useState(getStoredUserData);
  const [expandedSections, setExpandedSections] = useState(getStoredSections);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const accountMenuRef = useRef(null);
  // Catalogue produits: shown only to allow-listed tenants (the API answers
  // 404 for everyone else, so the section simply does not exist for them).
  const [catalogueEnabled, setCatalogueEnabled] = useState(false);
  const sidebarRef = useRef(null);

  useEffect(() => {
    setUserData(getStoredUserData());
  }, []);

  useEffect(() => {
    if (!['admin', 'employee'].includes(userRole)) return undefined;
    let cancelled = false;
    axios.get('/api/catalog/access', { headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } })
      .then((response) => { if (!cancelled) setCatalogueEnabled(!!response.data?.enabled); })
      .catch(() => { if (!cancelled) setCatalogueEnabled(false); });
    return () => { cancelled = true; };
  }, [userRole]);

  // « Mise en route » pinned on top while the account's onboarding is open
  // (signup, or started from Paramètres); refreshed after each step.
  const [onboarding, setOnboarding] = useState(null);
  useEffect(() => {
    if (userRole !== 'admin') return undefined;
    let cancelled = false;
    const load = () => axios.get('/api/setup/onboarding', { headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } })
      .then((response) => {
        if (cancelled) return;
        const data = response.data || {};
        setOnboarding(data.active ? { done: data.done || 0, total: data.total || 0 } : null);
      })
      .catch(() => { if (!cancelled) setOnboarding(null); });
    load();
    window.addEventListener(ONBOARDING_EVENT, load);
    return () => { cancelled = true; window.removeEventListener(ONBOARDING_EVENT, load); };
  }, [userRole]);

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('userData');
    navigate('/');
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => {
      const next = { ...prev, [section]: !prev[section] };
      try {
        localStorage.setItem(SECTIONS_STORAGE_KEY, JSON.stringify(next));
      } catch (error) {
        // Storage full/blocked: the toggle still works for this session.
      }
      return next;
    });
  };

  const location = useLocation();

  useEffect(() => {
    if (!accountMenuOpen) return undefined;
    const handleClickOutside = (event) => {
      if (accountMenuRef.current && !accountMenuRef.current.contains(event.target)) {
        setAccountMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [accountMenuOpen]);

  const has = (roles) => roles.includes(userRole);

  // Sidebar v3 (2026-10-07). Groups follow the day of the person using it:
  // the work itself (tickets, then orders), how it's going, then the business
  // the AI works for (clients, carriers, the channels customers use), the AI,
  // the plumbing, the settings. Per-item role gates are UNCHANGED; items only
  // moved between groups.
  const sections = [
    {
      key: 'tickets',
      label: t('sidebarNav.tickets', 'Tickets'),
      icon: <FaTicketAlt />,
      items: [
        has(['admin', 'employee', 'e-commerce', 'logistics', 'eshop', 'seller', 'experience'])
          && { to: userRole === 'logistics' ? '/dashboard-logs' : '/dashboard', icon: <FaHome />, label: t('dashboard') },
        has(['admin', 'e-commerce', 'logistics', 'eshop', 'experience'])
          && { to: '/tickets/archive', icon: <FaArchive />, label: t('archive') },
        // How the ticket list is cut into views: it belongs with the tickets.
        has(['admin', 'e-commerce'])
          && { to: '/ticket-views-parameters', icon: <FaEye />, label: t('ticketViewsParameters.title', 'Vues de tickets') },
      ],
    },
    // The orders behind the tickets and the money of their claims.
    {
      key: 'orders',
      label: t('sidebarNav.orders', 'Commandes'),
      icon: <FaShoppingCart />,
      items: [
        has(['admin', 'employee', 'logistics', 'logistician'])
          && { to: '/commerce/orders', icon: <FaShoppingCart />, label: t('commerceOrders.title', 'Commandes') },
        has(['admin', 'employee', 'logistics', 'logistician'])
          && { to: '/refunds', icon: <FaMoneyBillWave />, label: t('refunds.title', 'Remboursements') },
        // Legacy e-commerce product only
        has(['eshop', 'seller', 'experience'])
          && { to: '/orders', icon: <FaShoppingCart />, label: t('orders.title') },
        has(['eshop', 'seller', 'experience'])
          && { to: '/returns', icon: <FaExchangeAlt />, label: t('returns.title') },
        userRole === 'logistics'
          && { to: '/returns', icon: <FaExchangeAlt />, label: t('Adminlogistics') },
      ],
    },
    {
      key: 'pilotage',
      label: t('sidebarNav.pilotage', 'Pilotage'),
      icon: <FaChartLine />,
      items: [
        has(['admin', 'e-commerce'])
          && { to: '/statistics', icon: <FaChartBar />, label: t('statistics') },
        userRole === 'logistics'
          && { to: '/logistics-statistics', icon: <FaChartBar />, label: t('LogisticsStatistics') },
        userRole === 'seller'
          && { to: '/shop-statistics', icon: <FaChartBar />, label: t('sidebarNav.shopStatistics', 'Statistiques boutique') },
        has(['admin', 'e-commerce', 'seller', 'logistics', 'logistician', 'eshop', 'employee'])
          && { to: '/create-agent', icon: <FaUserTie />, label: t('employeesPage.navLabel', 'Employés') },
      ],
    },
    // The two kinds of clients of a logistician (Kevin, 2026-10-07): B2B — the
    // merchants it ships for (clients_edn: addresses, instructions, API) — and
    // B2C — the end customers who write in (the CRM).
    {
      key: 'clients',
      label: t('sidebarNav.clients', 'Clients'),
      icon: <FaUsers />,
      items: [
        userRole === 'admin' && !userData?.fa_account
          && { to: '/clients-instructions', icon: <FaBuilding />, label: t('sidebarNav.clientsB2B', 'Clients B2B') },
        has(['admin', 'employee'])
          && { to: '/clients', icon: <FaUser />, label: t('sidebarNav.clientsB2C', 'Clients B2C') },
        // Legacy e-commerce product only.
        has(['e-commerce', 'eshop', 'experience'])
          && { to: '/customers', icon: <FaUsers />, label: t('customers') },
      ],
    },
    // Everything about carriers and parcels in one place: the carriers, the
    // proactive parcel watch, the carrier follow-ups and the public tracking page.
    {
      key: 'carriers',
      label: t('sidebarNav.carriers', 'Transporteurs'),
      icon: <FaTruck />,
      items: [
        has(['admin', 'e-commerce', 'eshop', 'experience'])
          && { to: '/carriers-instructions', icon: <FaTruck />, label: t('sidebarNav.carrierList', 'Transporteurs') },
        has(['admin', 'logistics'])
          && { to: '/parcel-monitor', icon: <FaBoxOpen />, label: t('parcelMonitor.navLabel', 'Surveillance colis') },
        has(['admin', 'e-commerce', 'logistics', 'eshop', 'seller', 'experience'])
          && { to: '/followup-settings', icon: <FaClock />, label: t('sidebarNav.followups', 'Relances') },
        has(['admin', 'e-commerce', 'logistics', 'eshop', 'seller', 'experience'])
          && { to: '/tracking-settings', icon: <FaMapMarkedAlt />, label: t('sidebarNav.trackingPage', 'Page de suivi') },
      ],
    },
    // The surfaces the END CUSTOMER touches — chatbot, ticket form, the
    // e-mails and SMS sent to them — are channels to shape, not settings to
    // bury (Kevin, 2026-10-06 / 2026-10-07).
    has(['admin', 'e-commerce', 'logistics', 'eshop', 'seller', 'experience']) && {
      key: 'channels',
      label: t('sidebarNav.channels', 'Canaux clients'),
      icon: <FaComments />,
      items: [
        { to: '/chatbot-settings', icon: <FaComments />, label: t('chatbotSettings.title') },
        { to: '/ticket-form-settings', icon: <FaTicketAlt />, label: t('ticketFormSettings.navLabel', 'Formulaire de tickets') },
        { to: '/notifications-settings', icon: <FaBell />, label: t('sidebarNav.notifications', 'Modèles de notifications') },
        has(['admin', 'logistics'])
          && { to: '/sms-notifications', icon: <FaSms />, label: t('sidebarNav.smsNotifications', 'Notifications SMS') },
      ],
    },
    {
      key: 'agents',
      label: t('sidebarNav.agentsSection', 'Agents IA'),
      icon: <FaRobot />,
      items: [
        has(['admin', 'e-commerce', 'eshop', 'experience'])
          && { to: '/my-ai-assistants', icon: <FaRobot />, label: t('aiAgentsLabs') },
        userRole === 'logistics'
          && { to: '/logistics-ai-assistants', icon: <FaRobot />, label: t('LogisticsAiAssistants') },
        has(['admin', 'e-commerce', 'eshop', 'experience'])
          && { to: '/knowledge-board', icon: <FaBook />, label: t('aiAssistant.knowledgeBoard') },
        has(['admin', 'e-commerce'])
          && { to: '/ai-automation', icon: <FaBolt />, label: t('sidebarNav.automation', 'Automatisation') },
        has(['admin', 'e-commerce'])
          && { to: '/router-agent', icon: <FaProjectDiagram />, label: t('routerAgent.title', 'Agent Routeur') },
        userRole === 'admin'
          && { to: '/warehouses-instructions', icon: <FaWarehouse />, label: t('sidebarNav.warehouseInstructions', 'Instructions entrepôts') },
        has(['admin', 'e-commerce', 'logistics', 'eshop', 'seller', 'experience'])
          && { to: '/status-instructions', icon: <FaTasks />, label: t('statusInstructions.title', 'Instructions de statut') },
      ],
    },
    // Voice agents answer the phone: their own product (agents, numbers,
    // calls), not a tab of a text agent nor a page of the account settings
    // (Kevin, 2026-10-07).
    has(['admin']) && {
      key: 'voice',
      label: t('sidebarNav.voice', 'Agents vocaux'),
      icon: <FaHeadset />,
      items: [
        { to: '/voice-agents', icon: <FaHeadset />, label: t('sidebarNav.voiceAgents', 'Agents') },
        { to: '/voice-numbers', icon: <FaPhoneAlt />, label: t('sidebarNav.voiceNumbers', 'Numéros') },
        { to: '/voice-tickets', icon: <FaListUl />, label: t('sidebarNav.voiceCalls', 'Appels') },
      ],
    },
    {
      key: 'data',
      label: t('sidebarNav.dataTools', 'Données & outils'),
      icon: <FaDatabase />,
      items: [
        has(['admin', 'logistics'])
          && { to: '/connectors', icon: <FaDatabase />, label: t('sidebarNav.connectors', 'Sources de données') },
        // The one place a Shopify / PrestaShop shop is connected (orders,
        // parcel notes and tracking, catalogue).
        has(['admin', 'employee', 'logistics'])
          && { to: '/shops', icon: <FaStore />, label: t('sidebarNav.shops', 'Boutiques') },
        has(['admin', 'e-commerce', 'logistics', 'eshop', 'seller', 'experience'])
          && { to: '/brands', icon: <FaTag />, label: t('sidebarNav.brands', 'Marques') },
        has(['eshop', 'seller', 'experience'])
          && { to: '/products', icon: <FaStore />, label: t('products.title') },
        userRole === 'e-commerce'
          && { to: '/logistician', icon: <FaShippingFast />, label: t('logistician_bar') },
        has(['admin', 'e-commerce', 'eshop', 'experience'])
          && { to: '/openapi-schemes', icon: <FaCode />, label: t('sidebarNav.apiTools', 'API & outils') },
      ],
    },
    catalogueEnabled && {
      key: 'catalogue',
      label: t('catalogue.nav.title', 'Catalogue produits'),
      icon: <FaStore />,
      items: [
        { to: '/catalogue', icon: <FaBoxOpen />, label: t('catalogue.nav.products', 'Produits'), end: true },
        { to: '/catalogue?assistant=1', icon: <FaRobot />, label: t('catalogue.nav.assistant', 'Assistant marketing'), drawer: true },
        { to: '/catalogue/connections', icon: <FaPlug />, label: t('catalogue.nav.connections', 'Connexions') },
        { to: '/catalogue/settings', icon: <FaCog />, label: t('catalogue.nav.settings', 'Réglages') },
      ],
    },
    has(['admin', 'e-commerce', 'logistics', 'eshop', 'seller', 'experience']) && {
      key: 'settings',
      label: t('parameters') || 'Paramètres',
      icon: <FaCog />,
      items: [
        // Reachable here once finished or hidden; pinned on top while open.
        userRole === 'admin' && !onboarding
          && { to: '/onboarding', icon: <FaFlagCheckered />, label: t('sidebarNav.onboarding', 'Mise en route') },
        { to: '/parameters', icon: <FaCog />, label: t('sidebarNav.userParameters', 'Compte & préférences') },
        userRole === 'eshop'
          && { to: '/shopify-settings', icon: <FaShopify />, label: t('sidebarNav.shopifySettings', 'Paramètres Shopify') },
      ],
    },
  ];

  // The section holding the current page opens by itself, so the active link
  // is always visible (a deep link — journey, notification — lands anywhere).
  const activeSectionKey = (sections.filter(Boolean).find((section) => section.items.filter(Boolean).some((item) => {
    const base = String(item.to || '').split('?')[0];
    return base && (location.pathname === base || location.pathname.startsWith(`${base}/`));
  })) || {}).key;
  useEffect(() => {
    if (activeSectionKey && !expandedSections[activeSectionKey]) {
      setExpandedSections((prev) => ({ ...prev, [activeSectionKey]: true }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSectionKey]);

  const handleBrandClick = () => {
    if (userRole === 'logistics') {
      navigate('/dashboard-logs');
    } else {
      navigate('/dashboard');
    }
  };

  return (
    <aside ref={sidebarRef} className="sidebar">
      <header className="sidebar-header">
        <button type="button" className="sidebar-brand" onClick={handleBrandClick}>
          <BrandMark className="sidebar-brand-mark" />
          <span className="sidebar-brand-name">Unanimity</span>
        </button>
      </header>

      <nav className="sidebar-nav">
        {onboarding && (
          <NavLink to="/onboarding" className="sidebar-onboarding">
            <span className="sidebar-onboarding-top">
              <FaFlagCheckered aria-hidden="true" />
              <span className="sidebar-onboarding-label">{t('sidebarNav.onboarding', 'Mise en route')}</span>
              <span className="sidebar-onboarding-count">{onboarding.done}/{onboarding.total}</span>
            </span>
            <span className="sidebar-onboarding-meter" aria-hidden="true">
              <span style={{ width: `${onboarding.total ? Math.round((onboarding.done / onboarding.total) * 100) : 0}%` }} />
            </span>
          </NavLink>
        )}
        {sections.filter(Boolean).map((section) => {
          const items = section.items.filter(Boolean);
          if (items.length === 0) return null;
          return (
            <div className="sidebar-section" key={section.key}>
              <button
                type="button"
                className={`sidebar-section-header ${expandedSections[section.key] ? 'expanded' : ''}`}
                onClick={() => toggleSection(section.key)}
              >
                <span className="sidebar-section-icon">{section.icon}</span>
                <span className="sidebar-section-label">{section.label}</span>
                <FaChevronDown className="sidebar-section-chevron" />
              </button>
              {expandedSections[section.key] && (
                <ul className="sidebar-menu">
                  {items.map((item) => (
                    <li key={item.to}>
                      {item.drawer ? (
                        <Link to={item.to}>
                          <span className="sidebar-nav-icon">{item.icon}</span>
                          <span className="sidebar-nav-label">{item.label}</span>
                        </Link>
                      ) : (
                        <NavLink to={item.to} end={item.end}>
                          <span className="sidebar-nav-icon">{item.icon}</span>
                          <span className="sidebar-nav-label">{item.label}</span>
                        </NavLink>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </nav>

      <footer className="sidebar-footer">
        <div className="sidebar-account" ref={accountMenuRef}>
          {/* Identity opens a quiet account menu — sign-out is a once-a-month
              action, not the most prominent button on the screen. */}
          <button
            type="button"
            className="sidebar-user-info"
            onClick={() => setAccountMenuOpen((prev) => !prev)}
            aria-haspopup="menu"
            aria-expanded={accountMenuOpen}
            title={userData?.email || ''}
          >
            <div className="sidebar-user-avatar">
              {userData?.email?.charAt(0).toUpperCase() || 'U'}
            </div>
            <div className="sidebar-user-details">
              <span className="sidebar-user-email">{userData?.email || 'user@example.com'}</span>
              <span className="sidebar-user-role">{userRole || 'User'}</span>
            </div>
            <FaEllipsisV className="sidebar-account-caret" aria-hidden="true" />
          </button>
          <NotificationBell />
          {accountMenuOpen && (
            <div className="sidebar-account-menu" role="menu">
              <button
                type="button"
                role="menuitem"
                onClick={() => { setAccountMenuOpen(false); navigate('/parameters'); }}
              >
                <FaCog aria-hidden="true" />
                {t('sidebarNav.userParameters', 'Compte & préférences')}
              </button>
              <button
                type="button"
                role="menuitem"
                className="danger"
                onClick={handleLogout}
              >
                <FaPowerOff aria-hidden="true" />
                {t('logout', 'Déconnexion')}
              </button>
            </div>
          )}
        </div>
      </footer>
    </aside>
  );
};

export default Sidebar;
